import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtINR } from "@/lib/format";
import { usePrefs } from "@/contexts/PrefsContext";
import { toast } from "sonner";
import { AlertCircle, Sparkles, TrendingUp, GitMerge, X } from "lucide-react";

/**
 * Compact "Subscription Intelligence" panel — surfaces informational
 * flags on the user's own records. Never labels a subscription
 * unnecessary. Never cancels anything automatically.
 */

const FLAG_LABEL = {
    renewal_approaching:        "Renewal approaching",
    annual_renewal_approaching: "Annual renewal approaching",
    renewal_date_missing:       "Renewal date missing",
    billing_frequency_missing:  "Billing frequency missing",
    not_reviewed_recently:      "Not reviewed recently",
};

const FLAG_TONE = {
    renewal_approaching:        "bg-amber-50 text-amber-900 border-amber-200",
    annual_renewal_approaching: "bg-amber-50 text-amber-900 border-amber-200",
    renewal_date_missing:       "bg-stone-100 text-stone-700 border-stone-200",
    billing_frequency_missing:  "bg-stone-100 text-stone-700 border-stone-200",
    not_reviewed_recently:      "bg-blue-50 text-blue-800 border-blue-200",
};

export default function SubscriptionIntelligencePanel({ onOpenSub }) {
    const { prefs } = usePrefs();
    const hide = !!prefs?.hide_balance;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        api.get("/vault-calendar/subscription-intelligence")
            .then(r => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const dismissPair = async (a, b) => {
        try {
            await api.post("/vault-calendar/subscription-duplicates/dismiss",
                { a_id: a.id, b_id: b.id });
            toast.success("Dismissed");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't dismiss");
        }
    };

    if (loading) return null;
    if (!data || (!data.summary?.needs_review && !data.summary?.duplicate_pairs && !data.summary?.price_changes)) {
        return null;
    }

    const flaggedSubs = (data.subscriptions || []).filter(s => s.needs_review);

    return (
        <div className="mb-6 border border-stone-200 rounded-2xl overflow-hidden bg-white"
             data-testid="subscription-intelligence-panel">
            <div className="px-5 py-3 border-b border-stone-100 bg-gradient-to-r from-amber-50/70 via-white to-white flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Sparkles size={13} className="text-amber-800" />
                    <span className="text-[11px] uppercase tracking-widest text-amber-900 font-semibold">
                        Subscription Intelligence
                    </span>
                </div>
                <div className="text-[11px] text-stone-500">
                    {data.summary.needs_review} needs review · {data.summary.duplicate_pairs} possible dupe(s) · {data.summary.price_changes} price change(s)
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-stone-100">
                {/* Needs Review */}
                <div className="p-4">
                    <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold mb-2 flex items-center gap-1">
                        <AlertCircle size={11} /> Needs Review
                    </div>
                    {flaggedSubs.length === 0 ? (
                        <div className="text-[12px] text-stone-400">Nothing to review.</div>
                    ) : flaggedSubs.slice(0, 4).map(s => (
                        <button key={s.id} onClick={() => onOpenSub?.(s)}
                                className="block w-full text-left mb-2 pb-2 border-b border-stone-100 last:border-0 last:mb-0 last:pb-0 hover:bg-stone-50 rounded px-1 py-0.5"
                                data-testid={`sub-intel-review-${s.id}`}>
                            <div className="text-[13px] font-semibold text-stone-900 truncate">
                                {s.name || s.vendor}
                            </div>
                            <div className="flex flex-wrap gap-1 mt-1">
                                {(s.needs_review_flags || []).map(f => (
                                    <span key={f} className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${FLAG_TONE[f] || "bg-stone-100 text-stone-700 border-stone-200"}`}>
                                        {FLAG_LABEL[f] || f}
                                    </span>
                                ))}
                            </div>
                        </button>
                    ))}
                </div>

                {/* Duplicates */}
                <div className="p-4">
                    <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold mb-2 flex items-center gap-1">
                        <GitMerge size={11} /> Possible Duplicates
                    </div>
                    {(data.duplicates || []).length === 0 ? (
                        <div className="text-[12px] text-stone-400">None found.</div>
                    ) : (data.duplicates || []).slice(0, 3).map((d, i) => (
                        <div key={`${d.a.id}-${d.b.id}-${i}`}
                             className="mb-3 pb-3 border-b border-stone-100 last:border-0 last:mb-0 last:pb-0"
                             data-testid={`sub-intel-dupe-${i}`}>
                            <div className="text-[12.5px] text-stone-900">
                                <b>{d.a.name}</b> ↔ <b>{d.b.name}</b>
                            </div>
                            <div className="text-[10.5px] text-stone-500 mt-0.5">{d.reason}</div>
                            <div className="flex gap-1 mt-1.5">
                                <button onClick={() => onOpenSub?.(d.a)} className="text-[10.5px] font-semibold text-stone-600 hover:text-stone-900 px-2 py-0.5 rounded border border-stone-200">
                                    Compare
                                </button>
                                <button onClick={() => onOpenSub?.(d.a)} className="text-[10.5px] font-semibold text-stone-600 hover:text-stone-900 px-2 py-0.5 rounded border border-stone-200">
                                    Keep both
                                </button>
                                <button onClick={() => dismissPair(d.a, d.b)}
                                        className="text-[10.5px] font-semibold text-stone-500 hover:text-rose-700 px-2 py-0.5 rounded border border-stone-200 inline-flex items-center gap-0.5"
                                        data-testid={`sub-intel-dupe-dismiss-${i}`}>
                                    <X size={9} /> Dismiss
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Price Changes */}
                <div className="p-4">
                    <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold mb-2 flex items-center gap-1">
                        <TrendingUp size={11} /> Recorded Price Changes
                    </div>
                    {(data.price_changes || []).length === 0 ? (
                        <div className="text-[12px] text-stone-400">Nothing detected.</div>
                    ) : (data.price_changes || []).slice(0, 4).map(pc => (
                        <div key={pc.subscription_id} className="mb-2 pb-2 border-b border-stone-100 last:border-0 last:mb-0 last:pb-0"
                             data-testid={`sub-intel-price-${pc.subscription_id}`}>
                            <div className="text-[13px] font-semibold text-stone-900 truncate">{pc.name}</div>
                            <div className="text-[11px] text-stone-600 mt-0.5">
                                Previous: {hide ? "•••••" : fmtINR(pc.previous_amount)} → Current: <b>{hide ? "•••••" : fmtINR(pc.current_amount)}</b>
                                <span className={`ml-1.5 font-semibold ${pc.delta > 0 ? "text-rose-700" : "text-emerald-700"}`}>
                                    ({pc.delta > 0 ? "+" : ""}{hide ? "•••••" : fmtINR(pc.delta)})
                                </span>
                            </div>
                            <div className="text-[10px] text-stone-400 mt-0.5">
                                Based on stored transactions. Provider pricing not verified.
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="px-5 py-2 bg-stone-50 border-t border-stone-100 text-[10.5px] text-stone-500">
                {data.disclaimer}
            </div>
        </div>
    );
}
